/** @jsx jsx */
import { jsx } from 'theme-ui'
import React from 'react'
import { isEmpty } from '../../utils/obj'
import { useEntity } from '../helpers/selectors'

import ImgixImg from '../helpers/ev-imgix'
import { RichText, Link } from '../helpers/react'
import { Button } from '@uswitch/spark.button'
import ClickOutLinks from '../../components/click-out-links'

const richTextStyleOverrides = {
  '> *:first-child, > *:first-child > *:first-child': { mt: 0 },
  '> *:last-child, > *:last-child > *:last-child': { mb: 0 },
}
const outerElementMargin = 40
const innerElementMargin = 20

const CardImage = ({
  image,
  gridRow,
  columnNumber,
  isBorderCard,
  elementPadding,
  elementMargin,
}) => {
  const { image: imageEntity } = useEntity(image)
  const { description, file } = useEntity(imageEntity)
  const largeImage = false

  return (
    <div
      sx={{
        gridRow: gridRow,
        gridColumn: columnNumber,
        display: 'flex',
        justifyContent: isBorderCard ? 'flex-start' : 'center',
        ...(!largeImage && elementPadding),
        ...(largeImage ? { ...elementMargin, mt: 0 } : elementMargin),
      }}
    >
      <ImgixImg
        src={file?.url}
        alt={description}
        sx={{
          justifySelf: 'center',
          maxWidth: '100%',
          maxHeight: largeImage ? 'initial' : '75px',
          width: largeImage ? '100%' : 'auto',
          height: 'auto',
        }}
        imgixParams={{ fit: 'crop' }}
        height={largeImage ? 'initial' : 75}
      />
    </div>
  )
}

const CardHeading = ({
  gridRow,
  heading,
  columnNumber,
  isBorderCard,
  elementPadding,
  elementMargin,
  linkTo,
}) => {
  return (
    <Choose>
      <When condition={linkTo}>
        <Link
          href={linkTo}
          sx={{
            all: 'unset',
            gridRow: gridRow,
            gridColumn: columnNumber,
            cursor: 'pointer',
          }}
        >
          <RichText
            style={{
              textAlign: isBorderCard ? 'left' : 'center',
              ...richTextStyleOverrides,
              ...elementPadding,
              ...elementMargin,
            }}
            content={heading}
          />
        </Link>
      </When>

      <Otherwise>
        <RichText
          style={{
            gridRow: gridRow,
            gridColumn: columnNumber,
            textAlign: isBorderCard ? 'left' : 'center',
            ...richTextStyleOverrides,
            ...elementPadding,
            ...elementMargin,
          }}
          content={heading}
        />
      </Otherwise>
    </Choose>
  )
}
const CardSubheading = ({
  gridRow,
  subHeading,
  columnNumber,
  isBorderCard,
  elementPadding,
  elementMargin,
}) => {
  return (
    <RichText
      style={{
        gridRow: gridRow,
        gridColumn: columnNumber,
        px: elementPadding,
        textAlign: isBorderCard ? 'left' : 'center',
        mb: 12,
        ...elementPadding,
        ...richTextStyleOverrides,
        ...elementMargin,
      }}
      content={subHeading}
    />
  )
}
const CardBody = ({ gridRow, body, columnNumber, isBorderCard, elementPadding, elementMargin }) => {
  return (
    <RichText
      style={{
        gridRow: gridRow,
        textAlign: isBorderCard ? 'left' : 'center',
        gridColumn: columnNumber,
        ...elementPadding,
        ...richTextStyleOverrides,
        ...elementMargin,
      }}
      content={body}
    />
  )
}

const CardButton = (props) => {
  const {
    gridRow,
    buttonLink,
    buttonText,
    columnNumber,
    isBorderCard,
    elementMargin,
    clickoutLinkEntry,
  } = props
  const buttonMargin = isBorderCard ? 40 : 20
  return (
    <div
      sx={{
        gridRow: gridRow,
        display: 'flex',
        justifyContent: isBorderCard && !clickoutLinkEntry ? 'flex-start' : 'center',
        gridColumn: columnNumber,
        ...elementMargin,
      }}
    >
      {clickoutLinkEntry && !isEmpty(clickoutLinkEntry) ? (
        <ClickOutLinks {...clickoutLinkEntry} />
      ) : (
        <Button
          sx={{
            width: '100%',
            whiteSpace: 'break-spaces',
            textAlign: 'center',
            justifySelf: 'center',
            mx: buttonMargin,
          }}
          as='a'
          href={buttonLink}
        >
          {buttonText}
        </Button>
      )}
    </div>
  )
}

const buildCardComponentArray = ({
  heading,
  body,
  subHeading,
  image,
  buttonText,
  buttonLink,
  columnNumber,
  isBorderCard,
  variant,
  clickoutLink,
}) => {
  const clickoutLinkEntry = useEntity(clickoutLink)
  const components = []
  const elementPadding = { px: isBorderCard ? 40 : 20 }

  if (image)
    components.push(({ row, ...rest }) => (
      <CardImage
        gridRow={row}
        image={image}
        columnNumber={columnNumber}
        isBorderCard={isBorderCard}
        elementPadding={elementPadding}
        variant={variant}
        {...rest}
      />
    ))
  if (heading)
    components.push(({ row, ...rest }) => (
      <CardHeading
        gridRow={row}
        heading={heading}
        columnNumber={columnNumber}
        isBorderCard={isBorderCard}
        elementPadding={elementPadding}
        linkTo={buttonLink}
        {...rest}
      />
    ))
  if (subHeading)
    components.push(({ row, ...rest }) => (
      <CardSubheading
        gridRow={row}
        subHeading={subHeading}
        columnNumber={columnNumber}
        isBorderCard={isBorderCard}
        elementPadding={elementPadding}
        {...rest}
      />
    ))
  if (body)
    components.push(({ row, ...rest }) => (
      <CardBody
        gridRow={row}
        body={body}
        columnNumber={columnNumber}
        isBorderCard={isBorderCard}
        elementPadding={elementPadding}
        {...rest}
      />
    ))
  if ((buttonLink && buttonText) || (clickoutLinkEntry && !isEmpty(clickoutLinkEntry)))
    components.push(({ row, ...rest }) => (
      <CardButton
        gridRow={row}
        buttonLink={buttonLink}
        buttonText={buttonText}
        columnNumber={columnNumber}
        isBorderCard={isBorderCard}
        clickoutLinkEntry={clickoutLinkEntry}
        {...rest}
      />
    ))

  return components
}

const CardWithBorder = (props) => {
  const { columnNumber } = props

  const components = buildCardComponentArray(props)

  const gridRowContainerStyling = `1 / span ${components.length}`

  return (
    <>
      <div
        sx={{
          gridColumn: columnNumber,
          gridRow: gridRowContainerStyling,
          background: 'white',
          boxShadow: `5px 5px 20px 0 rgba(37,24,9,.2)`,
          py: ['0.1px', 0],
          mb: ['sm', 0],
        }}
      >
        <For each='Component' of={components} index='i'>
          <Choose>
            <When condition={i === 0}>
              {/* eslint-disable react/jsx-no-undef */}
              <Component
                key={i}
                row={i + 1}
                elementMargin={{ mt: outerElementMargin, mb: innerElementMargin }}
              />
            </When>
            <When condition={i + 1 === components.length}>
              {/* eslint-disable react/jsx-no-undef */}
              <Component key={i} row={i + 1} elementMargin={{ mb: outerElementMargin }} />
            </When>
            <Otherwise>
              {/* eslint-disable react/jsx-no-undef */}
              <Component key={i} row={i + 1} elementMargin={{ mb: innerElementMargin }} />
            </Otherwise>
          </Choose>
        </For>
      </div>
    </>
  )
}

const FeaturedCardsCard = (props) => {
  const {
    title: heading,
    subHeading,
    description: body,
    image,
    linkText: buttonText,
    linkHref: buttonLink,
    cardType,
    columnNumber,
    clickoutLink,
  } = props

  const mappedProps = {
    heading,
    body,
    image,
    buttonText,
    buttonLink,
    subHeading,
    columnNumber,
    isBorderCard: true,
    clickoutLink,
    variant: cardType,
  }

  return <CardWithBorder {...mappedProps} />
}

export default FeaturedCardsCard
